import { AnyAction, combineReducers } from 'redux'
import { createSelector } from 'reselect'

import {
  FETCH_FITNESS_PLAN,
  FETCH_FITNESS_PLAN_FULFILLED,
  FETCH_FITNESS_PLAN_TEMPLATES,
  FETCH_FITNESS_PLAN_TEMPLATES_FULFILLED
} from './actionTypes'
import { createPendingCounterReducer } from './common'

import {
  FetchFitnessPlanPayload,
  FitnessPlan,
  FitnessPlanForDate,
  FitnessPlanReduxState,
  FitnessPlanSlot,
  FitnessPlanTemplate,
  FitnessPlanWeek,
  PromiseAction,
  ReduxStoreState
} from 'Models'
import { coreBackendService } from 'Services'

export const fetchFitnessPlanAction = (payload: FetchFitnessPlanPayload): PromiseAction<FitnessPlanWeek> => ({
  type: FETCH_FITNESS_PLAN,
  payload: coreBackendService.fetchFitnessplan(payload)
})

export const fetchFitnessPlanTemplatesAction = (): PromiseAction<FitnessPlanTemplate[]> => ({
  type: FETCH_FITNESS_PLAN_TEMPLATES,
  payload: Promise.all([
    coreBackendService.fetchFitnessPlanTemplates({ system: false }),
    coreBackendService.fetchFitnessPlanTemplates({ system: true })
  ]).then(([userTemplates, systemTemplates]) => [...userTemplates, ...systemTemplates])
})

const initialPlans: FitnessPlanWeek = []
const initialTemplates: FitnessPlanTemplate[] = []

const sortFitnessPlans = (fitnessPlans: FitnessPlanWeek): FitnessPlanWeek => {
  return fitnessPlans
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((fitnessPlan) => ({
      ...fitnessPlan,
      slots: fitnessPlan.slots.slice().sort((a, b) => a.rank - b.rank)
    }))
}

export const getDayOffset = (weekDay: string): number => {
  const daySequence: { [key: string]: number } = {
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
    SUNDAY: 7
  }

  return daySequence[weekDay]
}

export const getSlotsForDate = (plan: FitnessPlan, targetDate: Date): FitnessPlanSlot[] => {
  const matchingSlots: FitnessPlanSlot[] = []
  const startDate = new Date(plan.startDate)
  const startDayOfWeek = startDate.getDay() === 0 ? 7 : startDate.getDay()
  const targetDateDayOfWeek = targetDate.getDay() === 0 ? 7 : targetDate.getDay()

  plan.slots.forEach((slot) => {
    const daysFromStartToSlot = (slot.week - 1) * 7 + getDayOffset(slot.weekDay) - startDayOfWeek

    const slotDate = new Date(startDate)
    slotDate.setDate(slotDate.getDate() + daysFromStartToSlot)

    if (
      slotDate.toDateString() === targetDate.toDateString() ||
      (!plan.endDate && getDayOffset(slot.weekDay) === targetDateDayOfWeek)
    ) {
      matchingSlots.push(slot)
    }
  })

  return matchingSlots
}

const buildTemplateNameById = (templates: FitnessPlanTemplate[]): Map<string, string> =>
  new Map(templates.map((template) => [template.id, template.name]))

const resolveDisplayName = (
  plan: { name?: string; templateId: string },
  index: number,
  planCount: number,
  templateNameById: Map<string, string>
): string => plan.name ?? templateNameById.get(plan.templateId) ?? ''

const assignDisplayNames = (
  plans: { id: string; name?: string; templateId: string; slots: FitnessPlanSlot[] }[],
  templateNameById: Map<string, string>
): FitnessPlanForDate[] =>
  plans.map((plan, index) => ({
    id: plan.id,
    slots: plan.slots,
    displayName: resolveDisplayName(plan, index, plans.length, templateNameById)
  }))

export const getFitnessPlansForDate = (
  fitnessPlans: FitnessPlanWeek,
  targetDate: Date,
  templates: FitnessPlanTemplate[] = []
): FitnessPlanForDate[] => {
  const templateNameById = buildTemplateNameById(templates)

  const plansWithSlots = sortFitnessPlans(fitnessPlans)
    .map((plan) => ({
      id: plan.id,
      name: plan.name,
      templateId: plan.templateId,
      slots: getSlotsForDate(plan, targetDate)
    }))
    .filter((plan) => plan.slots.length > 0)

  return assignDisplayNames(plansWithSlots, templateNameById)
}

const data = (state = initialPlans, { type, payload }: AnyAction): FitnessPlanWeek => {
  switch (type) {
    case FETCH_FITNESS_PLAN_FULFILLED: {
      return payload
    }

    default:
      return state
  }
}

const templates = (state = initialTemplates, { type, payload }: AnyAction): FitnessPlanTemplate[] => {
  switch (type) {
    case FETCH_FITNESS_PLAN_TEMPLATES_FULFILLED: {
      return payload
    }

    default:
      return state
  }
}

export const fitnessPlanWeekSelector = createSelector(
  [(state: ReduxStoreState) => state.fitnessPlan.data],
  (fitnessPlans) => sortFitnessPlans(fitnessPlans)
)

export const fitnessPlanTemplatesSelector = createSelector(
  [(state: ReduxStoreState) => state.fitnessPlan.templates],
  (templates) => templates
)

export const fitnessPlansForDateSelector = (targetDate: Date) =>
  createSelector([fitnessPlanWeekSelector, fitnessPlanTemplatesSelector], (fitnessPlans, templates) =>
    getFitnessPlansForDate(fitnessPlans, targetDate, templates)
  )

export const fitnessPlanSlotsForDateSelector = (targetDate: Date) =>
  createSelector([fitnessPlansForDateSelector(targetDate)], (plans) => plans.flatMap((plan) => plan.slots))

const asyncActionTypes = [FETCH_FITNESS_PLAN, FETCH_FITNESS_PLAN_TEMPLATES]

export default combineReducers<FitnessPlanReduxState>({
  data,
  templates,
  pendingCounter: createPendingCounterReducer(...asyncActionTypes)
})
